import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { OTPInput, type SlotProps } from "input-otp";
import { AlertCircle, Loader2 } from "lucide-react";

export function cn(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

type Variant = "primary" | "secondary" | "ghost";
const VARIANTS: Record<Variant, string> = {
  primary: "border border-signal bg-[rgba(255,29,0,0.16)] text-white shadow-[0_0_0_1px_rgba(255,29,0,0.35),0_0_18px_-4px_rgba(255,29,0,0.55)] hover:bg-[rgba(255,29,0,0.24)] hover:shadow-[0_0_0_1px_rgba(255,29,0,0.6),0_0_26px_-2px_rgba(255,29,0,0.7)] active:shadow-[0_0_10px_-4px_rgba(255,29,0,0.5)]",
  secondary: "border border-white/15 bg-white/[0.05] text-white backdrop-blur-xl hover:border-white/30 hover:bg-white/[0.09]",
  ghost: "border border-transparent bg-transparent text-zinc-300 hover:bg-white/[0.06] hover:text-white",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex h-11 items-center justify-center gap-2 rounded-[12px] px-5 text-sm font-semibold tracking-[-0.01em]",
        "transition-[background-color,box-shadow,border-color,transform] duration-200 ease-out active:scale-[0.98]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-ink",
        "disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:active:scale-100",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput(
  { className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        "h-11 w-full rounded-[12px] border border-white/15 bg-white/[0.045] px-3.5 text-[15px] text-white placeholder:text-zinc-500",
        "transition-[border-color,box-shadow,background-color] duration-150 hover:border-white/25 focus:border-signal focus:bg-white/[0.07] focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,29,0,0.22)]",
        "aria-[invalid=true]:border-red-400",
        className,
      )}
      {...rest}
    />
  );
});

export function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="label-caps block text-zinc-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function Slot(props: SlotProps) {
  return (
    <div
      className={cn(
        "relative flex h-14 w-11 items-center justify-center rounded-[12px] border bg-white/[0.05] text-2xl font-light text-white backdrop-blur-xl transition-[border-color,box-shadow] duration-150 sm:w-12",
        props.isActive ? "border-signal shadow-[0_0_0_3px_rgba(255,29,0,0.22),0_0_16px_-4px_rgba(255,29,0,0.6)]" : "border-white/15",
      )}
    >
      {props.char ?? <span className="text-zinc-600">{props.placeholderChar}</span>}
      {props.hasFakeCaret && <div className="absolute h-6 w-px animate-pulse bg-white" />}
    </div>
  );
}

interface CodeInputProps {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  testId: string;
  autoFocus?: boolean;
}

export function CodeInput({ value, onChange, onComplete, testId, autoFocus }: CodeInputProps) {
  return (
    <OTPInput
      maxLength={6}
      value={value}
      onChange={(v) => onChange(v.replace(/\D/g, ""))}
      onComplete={onComplete}
      inputMode="numeric"
      autoFocus={autoFocus}
      autoComplete="one-time-code"
      data-testid={testId}
      containerClassName="flex items-center gap-2"
      render={({ slots }) => (
        <>
          {slots.map((slot, i) => (
            <Slot key={i} {...slot} />
          ))}
        </>
      )}
    />
  );
}

export function ErrorText({ message, testId }: { message: string | null; testId: string }) {
  if (!message) return null;
  return (
    <div role="alert" data-testid={testId} className="flex items-start gap-2 rounded-[12px] border border-red-400/40 bg-red-400/10 px-3 py-2 text-sm text-red-200">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function DemoBadge({ show, testId = "demo-badge" }: { show: boolean; testId?: string }) {
  if (!show) return null;
  return (
    <span
      data-testid={testId}
      data-demo-badge="true"
      className="inline-flex items-center rounded-full border border-blue-400/50 bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200"
    >
      Demo
    </span>
  );
}

type Tone = "success" | "warning" | "info" | "neutral" | "danger";
const TONES: Record<Tone, string> = {
  success: "border-emerald-400/35 text-emerald-300", warning: "border-amber-400/35 text-amber-200",
  info: "border-sky-400/35 text-sky-200", neutral: "border-white/15 text-zinc-300", danger: "border-red-400/40 text-red-300",
};
const DOTS: Record<Tone, string> = { success: "bg-emerald-400", warning: "bg-amber-400", info: "bg-sky-400", neutral: "bg-zinc-400", danger: "bg-red-400" };
const SUCCESS = /^(ACTIVE|MATCHED|APPROVED|FINALIZED|RECONCILED|RECONCILED_PAID|SIGNED|COMPLETED|PASS|VERIFIED|AUTHORIZED|CLOCKED_IN|DELIVERED|SENT|RESOLVED|BUSINESS_APPROVED)$/;
const DANGER = /^(FAILED|REJECTED|BLOCKED|BLOCKED_COMPLIANCE|VOIDED|ERROR|UNDELIVERED|FAIL|OVERPAYMENT|LOCKED)$/;
const NEUTRAL = /^(DEACTIVATED|ARCHIVED|REVOKED|EXPIRED|CANCELED|CANCELLED|SUPERSEDED|DISABLED|WITHDRAWN|NO_AMOUNT_DUE|DRAFT|OPEN)$/;
const INFO = /^(SUBMITTED|SUBMITTED_REPORTED|SUBMITTED_FOR_APPROVAL|PENDING_RECONCILIATION|IN_REVIEW|PENDING_GPS|QUEUED|PARTIALLY_SUBMITTED|REOPENED)$/;

export function statusTone(status: string): Tone {
  if (SUCCESS.test(status)) return "success";
  if (DANGER.test(status)) return "danger";
  if (NEUTRAL.test(status)) return "neutral";
  if (INFO.test(status)) return "info";
  return "warning";
}

export function StatusPill({ status, label, testId }: { status: string; label?: string; testId?: string }) {
  const tone = statusTone(status);
  return (
    <span data-testid={testId ?? `status-${status.toLowerCase()}`}
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border bg-white/[0.04] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", TONES[tone])}>
      <span aria-hidden className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOTS[tone])} />
      {label ?? status.replace(/_/g, " ")}
    </span>
  );
}

export function FullScreenLoader() {
  return (
    <div data-testid="page-loader" className="min-h-[40vh] space-y-4 py-6" role="status">
      <div className="flex items-center gap-3 text-sm text-zinc-400"><Loader2 className="h-5 w-5 animate-spin text-signal" aria-label="Loading" />Loading…</div>
      <div className="skeleton h-24 w-full" />
      <div className="grid gap-4 sm:grid-cols-3"><div className="skeleton h-20" /><div className="skeleton h-20" /><div className="skeleton h-20" /></div>
    </div>
  );
}
